import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { AppLayout } from "../shared/layout";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import { staggerContainer, staggerItem } from "@/components/motion";
import {
  MessageCircle,
  Heart,
  Shield,
  CloudRain,
  Wrench,
  HandHeart,
  ArrowRight,
  Clock,
  Filter,
  Target,
  Zap,
  Trash2,
  Check,
  RotateCcw,
} from "lucide-react";
import {
  deletePracticeSession,
  deletePracticeSessions,
  getScenarios,
  getPracticeSessions,
  startPracticeSession,
  getCoachProfile,
  updateCoachProfile,
  generateCoachPlan,
} from "@/data/requests/practice-requests";
import { buildPlan, planWithProgress, NEXT_GOAL, needsCheckin } from "@/lib/personal-plan";

const CATEGORY_ICONS = {
  dating: Heart,
  texting: MessageCircle,
  boundaries: Shield,
  breakup: CloudRain,
  conflict: Wrench,
  rejection: HandHeart,
};

const CATEGORY_ORDER = ["all", "dating", "texting", "boundaries", "conflict", "breakup", "rejection"];

function getPracticeFocus(scenario, t) {
  const text = `${scenario.title} ${scenario.description} ${scenario.goalHint || ""}`.toLowerCase();
  if (text.includes("boundary") || text.includes("no")) return t("practice.focus.boundary");
  if (text.includes("breakup") || text.includes("ending")) return t("practice.focus.breakup");
  if (text.includes("text")) return t("practice.focus.texting");
  if (text.includes("fight") || text.includes("repair")) return t("practice.focus.repair");
  if (text.includes("rejection")) return t("practice.focus.rejection");
  return t("practice.focus.default");
}

function getBestFor(scenario, t) {
  const category = scenario.category;
  if (category === "texting") return t("practice.bestFor.texting");
  if (category === "boundaries") return t("practice.bestFor.boundaries");
  if (category === "breakup") return t("practice.bestFor.breakup");
  if (category === "conflict") return t("practice.bestFor.conflict");
  if (category === "dating") return t("practice.bestFor.dating");
  return t("practice.bestFor.default");
}

function getScenarioCopy(scenario, t) {
  return {
    title: t(`practice.scenarios.${scenario.slug}.title`, {
      defaultValue: scenario.title,
    }),
    description: t(`practice.scenarios.${scenario.slug}.description`, {
      defaultValue: scenario.description,
    }),
  };
}

export function PracticeScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [ownOpen, setOwnOpen] = useState(false);
  const [situation, setSituation] = useState("");
  const [sceneScenario, setSceneScenario] = useState(null);
  const [sceneIndex, setSceneIndex] = useState(0);
  const [sceneCustom, setSceneCustom] = useState(false);
  const [sceneCustomText, setSceneCustomText] = useState("");

  const { data: scenariosData, isLoading } = useQuery({
    queryKey: ["scenarios", i18n.language],
    queryFn: () => getScenarios(i18n.language),
  });

  const { data: sessionsData } = useQuery({
    queryKey: ["practice-sessions"],
    queryFn: getPracticeSessions,
  });

  const startMutation = useMutation({
    mutationFn: (payload) => startPracticeSession(payload, i18n.language),
    onSuccess: (res) => navigate(`/practice/${res.data.id}`),
    onError: (err) =>
      toast({
        title: t("practice.errors.couldNotStart"),
        description: err?.response?.data?.error || t("common.tryAgain"),
        variant: "destructive",
      }),
  });

  const deleteSessionMutation = useMutation({
    mutationFn: deletePracticeSession,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["practice-sessions"] });
      toast({ title: t("practice.history.deleted") });
    },
    onError: () =>
      toast({
        title: t("practice.history.deleteFailed"),
        variant: "destructive",
      }),
  });

  const deleteAllSessionsMutation = useMutation({
    mutationFn: deletePracticeSessions,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["practice-sessions"] });
      toast({ title: t("practice.history.cleared") });
    },
    onError: () =>
      toast({
        title: t("practice.history.deleteFailed"),
        variant: "destructive",
      }),
  });

  const scenarios = useMemo(() => scenariosData?.data || [], [scenariosData]);
  const allSessions = sessionsData?.data || [];
  const recentSessions = allSessions.slice(0, 5);

  // "For you" — the coach's plan from the personal setup, score-aware.
  const { data: coachData } = useQuery({ queryKey: ["coach-profile"], queryFn: getCoachProfile });
  const personal = coachData?.data?.personal;
  const storedPlan = coachData?.data?.plan;
  const planFresh =
    storedPlan?.steps?.length && storedPlan.goal === personal?.goal && storedPlan.status === personal?.status;
  const plan = useMemo(() => {
    if (!personal) return [];
    const journeySteps = t("journeys.list", { returnObjects: true });
    const steps = planFresh ? storedPlan.steps : buildPlan(personal);
    return planWithProgress(steps, allSessions, { journeySteps, since: personal.savedAt || Date.now() });
  }, [personal, sessionsData, t, planFresh, storedPlan]);
  const planDone = plan.filter((r) => r.state === "done").length;
  const planComplete = plan.length > 0 && planDone === plan.length;
  const nextGoal = planComplete ? NEXT_GOAL[personal?.goal] : null;
  const showCheckin = needsCheckin(personal);
  const [isSwitchingGoal, setIsSwitchingGoal] = useState(false);

  const startNextGoal = async () => {
    if (!nextGoal || isSwitchingGoal) return;
    try {
      setIsSwitchingGoal(true);
      await updateCoachProfile({
        personal: { ...personal, goal: nextGoal, savedAt: new Date().toISOString() },
      });
      try { await generateCoachPlan(i18n.language); } catch {}
      queryClient.invalidateQueries({ queryKey: ["coach-profile"] });
    } catch {
    } finally {
      setIsSwitchingGoal(false);
    }
  };

  // Profiles saved before savedAt existed: stamp once so old sessions
  // stop pre-ticking the plan.
  useEffect(() => {
    if (!personal || personal.savedAt) return;
    updateCoachProfile({ personal: { ...personal, savedAt: new Date().toISOString() } })
      .then(() => queryClient.invalidateQueries({ queryKey: ["coach-profile"] }))
      .catch(() => {});
  }, [personal]);

  const openPlanItem = (rec) => {
    if (rec.type === "journey") return navigate("/journeys");
    if (rec.type === "own")
      return startMutation.mutate({ situation: t(`personalSetup.prefills.${rec.prefillKey}`) });
    const sc = scenarios.find((s) => s.slug === rec.slug);
    // Plan-launched scenarios run in the user's own situation.
    if (sc) startMutation.mutate({ scenarioId: sc.id, personalize: true });
  };

  const availableCategories = useMemo(() => {
    const seen = new Set(scenarios.map((scenario) => scenario.category));
    return CATEGORY_ORDER.filter((category) => category === "all" || seen.has(category));
  }, [scenarios]);
  const filteredScenarios = useMemo(
    () =>
      selectedCategory === "all"
        ? scenarios
        : scenarios.filter((scenario) => scenario.category === selectedCategory),
    [scenarios, selectedCategory]
  );

  return (
    <AppLayout
      title={t("practice.pageTitle")}
      description={t("practice.pageDescription")}
      loading={isLoading}
    >
      <div className="space-y-10">
        {/* "For you" — the coach's plan with progress */}
        {plan.length > 0 && (
          <div className="rounded-xl border bg-card p-5" style={{ borderColor: "rgba(30,41,59,0.25)" }}>
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <Target className="h-4 w-4" style={{ color: "#1E293B" }} />
                <h3 className="font-bold text-sm">{t("practice.forYou.title")}</h3>
              </div>
              <span className="text-xs font-bold" style={{ color: "#1E293B" }}>
                {t("practice.forYou.done", { done: planDone, total: plan.length })}
              </span>
            </div>
            <p className="text-xs text-muted-foreground mb-2">{t("practice.forYou.sub")}</p>
            {personal.freeText && (
              <p
                className="text-xs italic text-muted-foreground mb-3 pl-2"
                style={{ borderLeft: "2px solid rgba(30,41,59,0.35)" }}
              >
                {t("practice.forYou.echo", {
                  quote: personal.freeText.length > 90 ? personal.freeText.slice(0, 90) + "…" : personal.freeText,
                })}
              </p>
            )}
            <div className="space-y-1">
              {plan.map((rec, idx) => {
                const isDone = rec.state === "done";
                const isRetry = rec.state === "retry";
                const label =
                  rec.type === "journey"
                    ? t(`journeys.list.${rec.journeyId}.name`, { defaultValue: t("practice.forYou.journeyTag") })
                    : rec.type === "own"
                      ? t(`personalSetup.goals.${personal?.goal}`, { defaultValue: t("practice.forYou.ownTag") })
                      : t(`practice.scenarios.${rec.slug}.title`, {
                          defaultValue: scenarios.find((s) => s.slug === rec.slug)?.title || rec.slug,
                        });
                const tag = rec.type === "journey" ? t("practice.forYou.journeyTag") : rec.type === "own" ? t("practice.forYou.ownTag") : null;
                return (
                  <button
                    key={rec.key}
                    onClick={() => openPlanItem(rec)}
                    disabled={startMutation.isPending}
                    className="w-full flex items-center gap-3 rounded-lg px-2 py-2 text-left transition hover:bg-muted/60"
                  >
                    <span
                      className="flex h-6 w-6 items-center justify-center rounded-full text-xs font-extrabold shrink-0"
                      style={{
                        backgroundColor: isDone ? "#1E293B" : isRetry ? "#FDEBD2" : "rgba(30,41,59,0.11)",
                        color: isDone ? "#fff" : isRetry ? "#B4771F" : "#1E293B",
                      }}
                    >
                      {isDone ? <Check className="h-3.5 w-3.5" /> : isRetry ? <RotateCcw className="h-3 w-3" /> : idx + 1}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className={`block text-sm font-semibold truncate ${isDone ? "line-through opacity-60" : ""}`}>
                        {label}
                      </span>
                      {isRetry ? (
                        <span className="block text-[11px] font-semibold" style={{ color: "#B4771F" }}>
                          {t("practice.forYou.retryHint", { score: rec.bestScore })}
                        </span>
                      ) : rec.why && !isDone ? (
                        <span className="block text-[11px] text-muted-foreground truncate">{rec.why}</span>
                      ) : null}
                    </span>
                    {tag && (
                      <span
                        className="text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0"
                        style={{ backgroundColor: "rgba(30,41,59,0.11)", color: "#1E293B" }}
                      >
                        {tag}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {planComplete && nextGoal && (
              <div className="mt-3 pt-3 border-t" style={{ borderColor: "rgba(30,41,59,0.15)" }}>
                <p className="text-sm font-medium mb-2">
                  {t("practice.forYou.nextCoach", {
                    goal: t(`personalSetup.goals.${personal?.goal}`, { defaultValue: personal?.goal }),
                    next: t(`personalSetup.goals.${nextGoal}`, { defaultValue: nextGoal }),
                  })}
                </p>
                <Button size="sm" className="text-white" style={{ backgroundColor: "#1E293B" }} disabled={isSwitchingGoal} onClick={startNextGoal}>
                  {t("practice.forYou.nextCta")}
                </Button>
              </div>
            )}

            {showCheckin && (
              <button
                onClick={() => navigate("/personal-setup")}
                className="mt-3 w-full text-center text-xs font-bold py-1.5"
                style={{ color: "#1E293B" }}
              >
                {t("practice.forYou.checkin")}
              </button>
            )}
          </div>
        )}

        {/* Freeform entry points */}
        <div>
          <h3 className="text-sm font-medium text-muted-foreground mb-3">{t("freeform.title")}</h3>
          <div className="grid gap-4 md:grid-cols-2">
            <button
              type="button"
              disabled={startMutation.isPending}
              onClick={() => startMutation.mutate({ mode: "open" })}
              className="text-left rounded-lg border bg-card p-5 transition hover:shadow-md disabled:opacity-60"
              style={{ borderLeft: "3px solid #5865F2" }}
            >
              <div className="flex items-center gap-3 mb-1">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg" style={{ backgroundColor: "color-mix(in srgb, #5865F2 14%, transparent)" }}>
                  <MessageCircle className="h-5 w-5" style={{ color: "#5865F2" }} />
                </div>
                <h4 className="font-semibold">{t("freeform.openTitle")}</h4>
              </div>
              <p className="text-sm text-muted-foreground">{t("freeform.openDesc")}</p>
            </button>

            <div className="rounded-lg border bg-card p-5" style={{ borderLeft: "3px solid #F04461" }}>
              <div className="flex items-center gap-3 mb-1">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg" style={{ backgroundColor: "color-mix(in srgb, #F04461 14%, transparent)" }}>
                  <Zap className="h-5 w-5" style={{ color: "#F04461" }} />
                </div>
                <h4 className="font-semibold">{t("freeform.ownTitle")}</h4>
              </div>
              <p className="text-sm text-muted-foreground">{t("freeform.ownDesc")}</p>
              {!ownOpen ? (
                <Button variant="outline" size="sm" className="mt-3" onClick={() => setOwnOpen(true)}>
                  {t("freeform.ownTitle")} <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                </Button>
              ) : (
                <div className="mt-3 space-y-2">
                  <textarea
                    autoFocus
                    value={situation}
                    onChange={(e) => setSituation(e.target.value)}
                    placeholder={t("freeform.ownPlaceholder")}
                    rows={3}
                    maxLength={2000}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      disabled={situation.trim().length < 8 || startMutation.isPending}
                      onClick={() => startMutation.mutate({ situation: situation.trim() })}
                    >
                      {t("freeform.start")}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => { setOwnOpen(false); setSituation(""); }}>
                      {t("freeform.cancel")}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="rounded-lg border bg-card p-4 md:p-5">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h3 className="font-medium flex items-center gap-2">
                <Target className="h-4 w-4 text-primary" />
                {t("practice.filterTitle")}
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                {t("practice.filterDescription")}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {availableCategories.map((category) => (
                <Button
                  key={category}
                  type="button"
                  size="sm"
                  variant={selectedCategory === category ? "default" : "outline"}
                  onClick={() => setSelectedCategory(category)}
                  className="h-9"
                >
                  {category === "all" && <Filter className="mr-1.5 h-3.5 w-3.5" />}
                  {t(`practice.categories.${category}`)}
                </Button>
              ))}
            </div>
          </div>
        </div>

        {recentSessions.length > 0 && (
          <div>
            <div className="mb-3 flex items-center justify-between gap-3">
              <h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Clock className="h-4 w-4" /> {t("practice.recentTitle")}
              </h3>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={deleteAllSessionsMutation.isPending}
                onClick={() => {
                  if (window.confirm(t("practice.history.confirmClear"))) {
                    deleteAllSessionsMutation.mutate();
                  }
                }}
              >
                <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                {t("practice.history.clearAll")}
              </Button>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
              {recentSessions.map((s) => {
                const score =
                  s.status === "COMPLETED" && s.feedback
                    ? Math.round(
                        (s.feedback.clarityScore +
                          s.feedback.empathyScore +
                          s.feedback.boundaryScore) /
                          3
                      )
                    : null;
                return (
                  <div
                    key={s.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(`/practice/${s.id}`)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        navigate(`/practice/${s.id}`);
                      }
                    }}
                    className="group relative shrink-0 w-64 text-left rounded-lg border bg-card p-3 hover:border-primary/40 transition-colors"
                  >
                    <span
                      role="button"
                      tabIndex={0}
                      aria-label={t("practice.history.deleteOne")}
                      onClick={(event) => {
                        event.stopPropagation();
                        if (window.confirm(t("practice.history.confirmDelete"))) {
                          deleteSessionMutation.mutate(s.id);
                        }
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          event.stopPropagation();
                          if (window.confirm(t("practice.history.confirmDelete"))) {
                            deleteSessionMutation.mutate(s.id);
                          }
                        }
                      }}
                      className="absolute right-2 top-2 inline-flex h-7 w-7 items-center justify-center rounded-full border bg-background text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </span>
                    <p className="font-medium text-sm truncate">
                      {s.scenario ? getScenarioCopy(s.scenario, t).title : ""}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {new Date(s.startedAt).toLocaleDateString()} · {t("practice.messageCount", { count: s._count?.messages ?? 0 })}
                    </p>
                    <div className="mt-2">
                      {score !== null ? (
                        <Badge className="bg-primary/10 text-primary" variant="secondary">
                          {t("practice.scored", { score })}
                        </Badge>
                      ) : (
                        <Badge variant="secondary">{t(`practice.status.${s.status?.toLowerCase() || "active"}`)}</Badge>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-muted-foreground">
              {t("practice.chooseScenario")}
            </h3>
            <span className="text-xs text-muted-foreground">
              {t("practice.availableCount", { count: filteredScenarios.length })}
            </span>
          </div>
          <motion.div
            className="grid gap-4 md:grid-cols-2 lg:grid-cols-3"
            variants={staggerContainer}
            initial="hidden"
            animate="show"
          >
            {filteredScenarios.map((scenario) => {
              const Icon = CATEGORY_ICONS[scenario.category] || MessageCircle;
              const copy = getScenarioCopy(scenario, t);
              return (
                <motion.div key={scenario.id} variants={staggerItem}>
                  <Card className="flex flex-col h-full transition-shadow hover:shadow-md rounded-lg">
                    <CardHeader>
                      <div className="flex items-center justify-between mb-1">
                        <div className="w-11 h-11 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Icon className="h-5 w-5 text-primary" />
                        </div>
                        <Badge variant="secondary" className="text-xs">
                          {t(`practice.difficulty.${scenario.difficulty || 1}`)}
                        </Badge>
                      </div>
                      <CardTitle className="text-lg">{copy.title}</CardTitle>
                      <CardDescription>{copy.description}</CardDescription>
                    </CardHeader>
                    <CardContent className="mt-auto space-y-4">
                      <div className="space-y-2 text-xs text-muted-foreground">
                        <div className="flex gap-2">
                          <Target className="mt-0.5 h-3.5 w-3.5 text-primary shrink-0" />
                          <span>
                            {t("practice.practicePrefix")} <span className="text-foreground">{getPracticeFocus(scenario, t)}</span>
                          </span>
                        </div>
                        <div className="flex gap-2">
                          <Zap className="mt-0.5 h-3.5 w-3.5 text-primary shrink-0" />
                          <span>
                            {t(`practice.pushback.${scenario.difficulty || 1}`)} · {getBestFor(scenario, t)}
                          </span>
                        </div>
                      </div>
                      <Button
                        className="w-full"
                        variant="default"
                        disabled={startMutation.isPending}
                        onClick={() => {
                          if (scenario.sceneSeeds?.length) {
                            setSceneScenario(scenario);
                            setSceneIndex(0);
                            setSceneCustom(false);
                            setSceneCustomText("");
                          } else {
                            startMutation.mutate(scenario.id);
                          }
                        }}
                      >
                        {t("practice.practiceThis")} <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </motion.div>
        </div>

        {sceneScenario && (
          <div
            className="fixed inset-0 z-50 flex items-end justify-center bg-slate-950/45 p-0 md:items-center md:p-6"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) setSceneScenario(null);
            }}
          >
            <div className="w-full rounded-t-3xl border bg-background p-6 shadow-2xl md:max-w-lg md:rounded-3xl">
              <div className="mb-3 flex items-center gap-2 text-xs font-black tracking-widest text-primary">
                <Target className="h-4 w-4" />
                {t("practice.scene.label")}
              </div>
              <h3 className="text-2xl font-semibold">{getScenarioCopy(sceneScenario, t).title}</h3>

              {!sceneCustom ? (
                <>
                  <p className="mt-4 min-h-24 text-base leading-7 text-muted-foreground">
                    {sceneScenario.sceneSeeds?.[sceneIndex]}
                  </p>
                  <Button
                    className="mt-5 w-full"
                    size="lg"
                    disabled={startMutation.isPending}
                    onClick={() =>
                      startMutation.mutate({
                        scenarioId: sceneScenario.id,
                        situation: sceneScenario.sceneSeeds?.[sceneIndex],
                      })
                    }
                  >
                    {t("practice.scene.start")} <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <Button
                      variant="outline"
                      onClick={() =>
                        setSceneIndex((current) => (current + 1) % (sceneScenario.sceneSeeds?.length || 1))
                      }
                    >
                      <RotateCcw className="mr-2 h-4 w-4" /> {t("practice.scene.change")}
                    </Button>
                    <Button variant="outline" onClick={() => setSceneCustom(true)}>
                      {t("practice.scene.own")}
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <p className="mt-3 text-sm text-muted-foreground">{t("practice.scene.ownHint")}</p>
                  <textarea
                    autoFocus
                    value={sceneCustomText}
                    onChange={(event) => setSceneCustomText(event.target.value)}
                    placeholder={t("practice.scene.placeholder")}
                    rows={4}
                    maxLength={2000}
                    className="mt-4 w-full rounded-xl border border-input bg-background px-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                  <div className="mt-3 flex gap-2">
                    <Button
                      className="flex-1"
                      disabled={sceneCustomText.trim().length < 8 || startMutation.isPending}
                      onClick={() =>
                        startMutation.mutate({ scenarioId: sceneScenario.id, situation: sceneCustomText.trim() })
                      }
                    >
                      {t("practice.scene.start")}
                    </Button>
                    <Button variant="outline" onClick={() => setSceneCustom(false)}>
                      {t("freeform.cancel")}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        <p className="text-xs text-muted-foreground text-center max-w-md mx-auto">
          {t("practice.disclaimer")}
        </p>
      </div>
    </AppLayout>
  );
}
